import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, newIdempotencyKey } from '@/lib/api'
import type {
  Contract,
  Dispute,
  ExpertProfile,
  JobPost,
  MarketplaceCategory,
  Milestone,
  PageMeta,
  Proposal,
  TalentProfile,
} from '@/lib/types'

// --- Papan proyek ----------------------------------------------------------

export interface JobFilters {
  q?: string
  category?: string
  skill?: string
  min_budget?: number
  page?: number
}

export function useJobs(filters: JobFilters) {
  return useQuery({
    queryKey: ['jobs', filters],
    queryFn: () => api.get<{ job_posts: JobPost[]; meta: PageMeta }>('/job-posts', {
      query: { ...filters, per_page: 12 },
    }),
  })
}

export function useMyJobs() {
  return useQuery({
    queryKey: ['my-jobs'],
    queryFn: () => api.get<{ job_posts: JobPost[]; meta: PageMeta }>('/me/job-posts', { query: { per_page: 50 } }),
  })
}

export function useJob(jobId: string) {
  return useQuery({
    queryKey: ['job', jobId],
    queryFn: () => api.get<{ job_post: JobPost }>(`/job-posts/${jobId}`),
    select: (data) => data.job_post,
  })
}

export interface CreateJobInput {
  title: string
  description: string
  category: string
  skills: string[]
  budget_min: number
  budget_max: number
  duration_days?: number
}

export function useCreateJob() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateJobInput) => api.post<{ job_post: JobPost }>('/job-posts', input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['my-jobs'] })
      client.invalidateQueries({ queryKey: ['jobs'] })
    },
  })
}

export function useCloseJob(jobId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: () => api.post<{ job_post: JobPost }>(`/job-posts/${jobId}/close`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['job', jobId] })
      client.invalidateQueries({ queryKey: ['my-jobs'] })
    },
  })
}

// --- Proposal --------------------------------------------------------------

export function useMyProposals() {
  return useQuery({
    queryKey: ['my-proposals'],
    queryFn: () => api.get<{ proposals: Proposal[]; meta: PageMeta }>('/me/proposals', { query: { per_page: 50 } }),
  })
}

export function useSubmitProposal(jobId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { cover_letter: string; amount: number; duration_days: number }) =>
      api.post<{ proposal: Proposal }>(`/job-posts/${jobId}/proposals`, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['job', jobId] })
      client.invalidateQueries({ queryKey: ['my-proposals'] })
    },
  })
}

export interface MilestonePlanInput {
  title: string
  description?: string
  amount: number
}

export function useAcceptProposal() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { proposalId: string; milestones: MilestonePlanInput[] }) =>
      api.post<{ contract: Contract }>(`/proposals/${input.proposalId}/accept`, { milestones: input.milestones }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['job'] })
      client.invalidateQueries({ queryKey: ['contracts'] })
      client.invalidateQueries({ queryKey: ['my-jobs'] })
    },
  })
}

// --- Kontrak & escrow ------------------------------------------------------

export function useContracts(filters: { role?: string; state?: string } = {}) {
  return useQuery({
    queryKey: ['contracts', filters],
    queryFn: () => api.get<{ contracts: Contract[]; meta: PageMeta }>('/contracts', {
      query: { ...filters, per_page: 30 },
    }),
  })
}

export function useContract(contractId: string) {
  return useQuery({
    queryKey: ['contract', contractId],
    queryFn: () => api.get<{ contract: Contract }>(`/contracts/${contractId}`),
    select: (data) => data.contract,
  })
}

function useContractMutation<TInput, TData>(contractId: string, mutationFn: (input: TInput) => Promise<TData>) {
  const client = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['contract', contractId] })
      client.invalidateQueries({ queryKey: ['contracts'] })
      client.invalidateQueries({ queryKey: ['wallet'] })
      client.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useFundContract(contractId: string) {
  return useContractMutation<void, { contract: Contract }>(contractId, () =>
    api.post<{ contract: Contract }>(`/contracts/${contractId}/fund`, undefined, {
      idempotencyKey: newIdempotencyKey(),
    }),
  )
}

export function useSubmitMilestone(contractId: string) {
  return useContractMutation<{ milestoneId: string; note: string; files?: Array<{ name: string; url: string }> }, { milestone: Milestone }>(
    contractId,
    (input: { milestoneId: string; note: string; files?: Array<{ name: string; url: string }> }) =>
      api.post<{ milestone: Milestone }>(`/milestones/${input.milestoneId}/submit`, {
        note: input.note,
        files: input.files ?? [],
      }),
  )
}

export interface ReleaseResponse {
  milestone_id: string
  state: string
  released: {
    gross_amount: number
    platform_fee: number
    tax_withheld: number
    net_to_expert: number
    currency: string
  }
  ledger_transaction_id: string
  already_released: boolean
}

export function useApproveMilestone(contractId: string) {
  return useContractMutation<string, ReleaseResponse>(contractId, (milestoneId: string) =>
    api.post<ReleaseResponse>(`/milestones/${milestoneId}/approve`, undefined, {
      idempotencyKey: newIdempotencyKey(),
    }),
  )
}

export function useRequestRevision(contractId: string) {
  return useContractMutation<{ milestoneId: string; note: string }, unknown>(contractId, (input) =>
    api.post(`/milestones/${input.milestoneId}/request-revision`, { note: input.note }),
  )
}

export function useOpenDispute(contractId: string) {
  return useContractMutation<{ milestoneId: string; reason: string }, unknown>(contractId, (input) =>
    api.post(`/milestones/${input.milestoneId}/dispute`, { reason: input.reason }),
  )
}

export function useSubmitEvidence(contractId: string) {
  return useContractMutation<{ disputeId: string; note: string }, unknown>(contractId, (input) =>
    api.post(`/disputes/${input.disputeId}/evidence`, { note: input.note, files: [] }),
  )
}

// --- Mediasi ---------------------------------------------------------------

export function useDisputeQueue(state = 'open') {
  return useQuery({
    queryKey: ['disputes', state],
    queryFn: () => api.get<{ disputes: Dispute[]; meta: PageMeta }>('/admin/disputes', {
      query: { state, per_page: 30 },
    }),
  })
}

export function useDispute(disputeId: string) {
  return useQuery({
    queryKey: ['dispute', disputeId],
    queryFn: () =>
      api.get<{ dispute: Dispute; milestone: Milestone; contract: Contract }>(`/disputes/${disputeId}`),
  })
}

export function useRuleDispute(disputeId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { expert_ratio: number; client_ratio: number; reasoning: string }) =>
      api.post<{ expert_share: number; client_share: number; net_to_expert: number }>(
        `/disputes/${disputeId}/ruling`,
        input,
      ),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['dispute', disputeId] })
      client.invalidateQueries({ queryKey: ['disputes'] })
      client.invalidateQueries({ queryKey: ['contracts'] })
    },
  })
}

// --- Profil tenaga ahli ----------------------------------------------------

export function useExperts(filters: { q?: string; skill?: string; page?: number }) {
  return useQuery({
    queryKey: ['experts', filters],
    queryFn: () => api.get<{ experts: ExpertProfile[]; meta: PageMeta }>('/experts', {
      query: { ...filters, per_page: 12 },
    }),
  })
}

/**
 * useExpert membaca profil publik seorang talenta.
 *
 * Endpoint ini mengembalikan profil lengkapnya — termasuk pengalaman,
 * pendidikan, dan portofolio — bukan ringkasan seperti pada daftar. Tipenya
 * sempat tertulis ExpertProfile, sehingga portofolio yang sebenarnya sudah
 * terkirim tidak pernah terlihat di sisi peramban.
 */
export function useExpert(userId: string) {
  return useQuery({
    queryKey: ['expert', userId],
    queryFn: () => api.get<{ expert: TalentProfile }>(`/experts/${userId}`),
    select: (data) => data.expert,
  })
}

export function useSaveExpertProfile() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: {
      headline: string
      bio: string
      skills: string[]
      city: string
      hourly_rate?: number
      availability: string
    }) => api.put<{ expert: ExpertProfile }>('/me/expert-profile', input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['experts'] })
      client.invalidateQueries({ queryKey: ['expert'] })
    },
  })
}

// --- Taksonomi & profil talenta --------------------------------------------

export function useMarketplaceCategories() {
  return useQuery({
    queryKey: ['marketplace-categories'],
    queryFn: () => api.get<{ categories: MarketplaceCategory[] }>('/marketplace/categories'),
    select: (data) => data.categories,
    // Taksonomi bidang jarang berubah; menyimpannya lebih lama menghindari
    // permintaan berulang pada setiap halaman yang memakai penyaring.
    staleTime: 30 * 60_000,
  })
}

export function useTalentProfile(userId: string | undefined) {
  return useQuery({
    queryKey: ['talent-profile', userId],
    queryFn: () => api.get<{ expert: TalentProfile }>(`/experts/${userId}`),
    select: (data) => data.expert,
    enabled: Boolean(userId),
  })
}

export interface TalentDetailsInput {
  years_experience: number
  resume_url?: string
  portfolio_url?: string
  open_to_work: boolean
  preferred_employment: string[]
}

export function useSaveTalentDetails() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: TalentDetailsInput) => api.put<{ expert: TalentProfile }>('/me/talent-profile', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['talent-profile'] }),
  })
}

export function useAddExperience() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: {
      company_name: string
      role_title: string
      started_on: string
      ended_on?: string
      is_current: boolean
      description?: string
    }) => api.post<unknown>('/me/experiences', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['talent-profile'] }),
  })
}

export function useAddEducation() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: {
      institution: string
      degree?: string
      field?: string
      start_year?: number
      end_year?: number
    }) => api.post<unknown>('/me/educations', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['talent-profile'] }),
  })
}

export function useAddPortfolio() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { title: string; description?: string; url?: string; image_url?: string }) =>
      api.post<unknown>('/me/portfolios', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['talent-profile'] }),
  })
}

/**
 * useUploadPortfolioImage mengunggah foto sebuah karya.
 *
 * Unggahan multipart tidak lewat klien JSON: badannya dibentuk peramban, dan
 * menyetel Content-Type sendiri justru menghapus batas multipart yang ikut
 * dihasilkannya.
 */
export function useUploadPortfolioImage() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async ({ itemId, file }: { itemId: string; file: File }) => {
      const body = new FormData()
      body.append('file', file)

      const response = await fetch(`/api/v1/me/portfolios/${itemId}/image`, {
        method: 'POST',
        credentials: 'include',
        body,
      })

      const payload = await response.json()
      if (!response.ok) {
        throw new Error(payload?.error?.message ?? 'Foto gagal diunggah.')
      }
      return payload as { portfolio: { id: string; image_url?: string } }
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['talent-profile'] }),
  })
}

export function useRemovePortfolioImage() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (itemId: string) => api.delete<unknown>(`/me/portfolios/${itemId}/image`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['talent-profile'] }),
  })
}

export function useDeleteTalentItem(kind: 'experiences' | 'educations' | 'portfolios') {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (itemId: string) => api.delete<void>(`/me/${kind}/${itemId}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['talent-profile'] }),
  })
}
